import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ConfigService } from './config.service.js';
import { ViewerCoreService } from './viewer-core.service.js';
import { OctreeLoader } from './loader/octree-loader';
import { EptLoader } from './loader/ept-loader';
import { CopcLoader } from './loader/copc-loader';
import { createPointCloudOctree, type PotreePointCloudOctree } from './pointcloud-runtime.js';

export type PointCloudFormat = 'potree2' | 'ept' | 'copc';

export interface PointCloudLoadEvent {
  type: 'pointcloud_loaded';
  pointcloud: PotreePointCloudOctree;
  format: PointCloudFormat;
  url: string;
}

/**
 * Point cloud loader service providing Observable-based APIs for:
 *
 * - Automatic format detection from URL
 * - Observable streams with error propagation
 * - Abort-backed cancellation on unsubscribe
 * - Lifecycle-aware token handling (tokens read from ConfigService)
 */
@Injectable({ providedIn: 'root' })
export class PointCloudLoaderService {
  private config = inject(ConfigService);
  private core = inject(ViewerCoreService);

  /**
   * Detect the point cloud format from a URL path.
   */
  detectFormat(url: string): PointCloudFormat | null {
    const path = url.split(/[?#]/, 1)[0].toLowerCase();
    if (/(?:^|\/)ept\.json$/.test(path)) return 'ept';
    if (path.endsWith('.copc.laz')) return 'copc';
    if (/(?:^|\/)metadata\.json$/.test(path)) return 'potree2';
    return null;
  }

  /**
   * Load a point cloud from the given URL.
   *
   * Returns an Observable that emits a single PointCloudLoadEvent on success,
   * then completes. Unsubscribing aborts pending fetches where the underlying
   * loader supports AbortSignal.
   *
   * @param url - URL to the point cloud metadata file
   * @param options.format - Override auto-detected format
   */
  load(url: string, options?: { format?: PointCloudFormat }): Observable<PointCloudLoadEvent> {
    const format = options?.format ?? this.detectFormat(url);
    if (!format) {
      return throwError(() => new Error(`Unsupported point cloud format for URL: ${url}`));
    }

    return new Observable<PointCloudLoadEvent>(subscriber => {
      const controller = new AbortController();

      this.loadByFormat(url, format, controller.signal)
        .then(pointcloud => {
          if (subscriber.closed) {
            return;
          }

          subscriber.next({
            type: 'pointcloud_loaded',
            pointcloud,
            format,
            url,
          });
          subscriber.complete();
        })
        .catch(error => {
          if (controller.signal.aborted || error?.name === 'AbortError') {
            subscriber.complete();
            return;
          }

          subscriber.error(error);
        });

      return () => controller.abort();
    });
  }

  /**
   * Load a point cloud and add it to the active viewer scene.
   *
   * @param url - URL to the point cloud metadata file
   * @param options.format - Override format detection
   * @param options.pointSize - Initial point size (default 0.6)
   * @param options.activeAttribute - Initial active attribute (default 'rgba')
   * @param options.fitToScreen - Fit camera to point cloud after load (default true)
   */
  loadAndAdd(
    url: string,
    options?: {
      format?: PointCloudFormat;
      pointSize?: number;
      activeAttribute?: string;
      fitToScreen?: boolean;
    },
  ): Observable<PointCloudLoadEvent> {
    return this.load(url, { format: options?.format }).pipe(
      tap(event => {
        const viewer = this.core.viewer();
        if (!viewer) return;

        const pointcloud = event.pointcloud as any;
        const material = pointcloud.material;
        if (material) {
          material.size = options?.pointSize ?? 0.6;
          material.pointSizeType = 2; // ADAPTIVE
          material.shape = 0; // SQUARE
          material.activeAttributeName = options?.activeAttribute ?? 'rgba';
        }

        viewer.scene.addPointCloud(pointcloud);

        if (options?.fitToScreen !== false) {
          try {
            viewer.fitToScreen(0.6);
          } catch {
            console.warn('Failed to fit camera to point cloud');
          }
        }
      }),
    );
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private async loadByFormat(
    url: string,
    format: PointCloudFormat,
    signal?: AbortSignal,
  ): Promise<PotreePointCloudOctree> {
    switch (format) {
      case 'potree2':
        return this.loadPotree2(url, signal);
      case 'ept':
        return this.loadEpt(url, signal);
      case 'copc':
        return this.loadCopc(url, signal);
    }
  }

  private async loadPotree2(url: string, signal?: AbortSignal): Promise<PotreePointCloudOctree> {
    const result = await OctreeLoader.load(url, signal);
    const geometry = result?.geometry;
    if (!geometry) {
      throw new Error(`Failed to load Potree2 point cloud from: ${url}`);
    }
    const pointcloud = createPointCloudOctree(geometry);
    const aPosition = pointcloud.getAttribute('position');
    if (aPosition?.range && Array.isArray(aPosition.range[0])) {
      const [min, max] = aPosition.range as [number[], number[]];
      pointcloud.material.elevationRange = [min[2], max[2]];
    }
    return pointcloud;
  }

  private async loadEpt(url: string, signal?: AbortSignal): Promise<PotreePointCloudOctree> {
    const geometry = await EptLoader.load(url, signal);
    if (!geometry) {
      throw new Error(`Failed to load EPT point cloud from: ${url}`);
    }
    return createPointCloudOctree(geometry);
  }

  private async loadCopc(url: string, signal?: AbortSignal): Promise<PotreePointCloudOctree> {
    const geometry = await CopcLoader.load(url, signal);
    if (!geometry) {
      throw new Error(`Failed to load COPC point cloud from: ${url}`);
    }
    return createPointCloudOctree(geometry);
  }
}
